<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto bg-taupe-50"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-6 sm:pt-8 lg:pt-10 xl:max-w-7xl xl:mx-auto w-full"
        >
            <h1 id="primary-heading" class="sr-only">Admin: Projects</h1>
            <div class="px-4 sm:px-6 lg:px-8 pb-12 space-y-8">
                <!-- Header with tabs -->
                <div class="mb-6">
                    <admin-header-tabs current="projects" />
                </div>

                <!-- Page Title and Action -->
                <div
                    class="flex items-center justify-between mb-6 min-h-[2.5rem]"
                >
                    <h2 class="text-2xl font-medium text-taupe-900">
                        Projects
                    </h2>
                    <div class="flex-shrink-0">
                        <zen-button
                            v-if="!loading && can(PERMISSIONS.PROJECT_CREATE)"
                            @click="openCreateModal()"
                            variant="primary"
                        >
                            <template #icon>
                                <svg
                                    class="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z"
                                    />
                                </svg>
                            </template>
                            Create Project
                        </zen-button>
                    </div>
                </div>

                <zen-loading-indicator v-if="loading" />

                <!-- Projects Table -->
                <zen-grid v-if="!loading && hasResults">
                    <zen-grid-table>
                        <zen-grid-header>
                            <zen-grid-header-row>
                                <zen-grid-header-cell
                                    :hidable="false"
                                    title="Name"
                                />
                                <zen-grid-header-cell title="Key" />
                                <zen-grid-header-cell
                                    alignment="right"
                                    title="Actions"
                                />
                            </zen-grid-header-row>
                        </zen-grid-header>
                        <zen-grid-body>
                            <zen-grid-body-row
                                v-for="result in results"
                                :key="result.ulid"
                            >
                                <zen-grid-body-cell
                                    :hidable="false"
                                    :primary="true"
                                >
                                    <router-link
                                        :to="{
                                            name: 'AdminProjectsSingleEnvironmentsScreen',
                                            params: { projectKey: result.key },
                                        }"
                                        class="font-medium text-sky-600 hover:text-sky-500"
                                    >
                                        {{ result.name }}
                                    </router-link>
                                    <div
                                        class="lg:hidden text-sm text-taupe-500 font-mono"
                                    >
                                        {{ result.key }}
                                    </div>
                                </zen-grid-body-cell>
                                <zen-grid-body-cell>
                                    <span
                                        class="font-mono text-sm text-taupe-900"
                                        >{{ result.key }}</span
                                    >
                                </zen-grid-body-cell>
                                <zen-grid-body-cell alignment="right">
                                    <div class="flex justify-end gap-2">
                                        <zen-table-icon
                                            v-if="
                                                can(PERMISSIONS.PROJECT_DELETE)
                                            "
                                            @click="openRemoveModal(result.key)"
                                            :srText="'Remove ' + result.name"
                                            tooltipText="Remove"
                                        >
                                            <zen-icon-remove />
                                        </zen-table-icon>
                                    </div>
                                </zen-grid-body-cell>
                            </zen-grid-body-row>
                        </zen-grid-body>
                    </zen-grid-table>
                </zen-grid>

                <zen-empty-table-message
                    v-if="!loading && !hasResults"
                    title="No projects yet"
                    icon="ZenIconFolder"
                >
                    There are no projects.
                </zen-empty-table-message>
            </div>
        </section>

        <create-project-slide-in
            :is-open="modals.create.show"
            v-model:name="modals.create.input.name"
            :processing="modals.create.processing"
            @close="closeCreateModal"
            @confirm="confirmCreate"
        />

        <remove-project-modal
            :is-open="modals.remove.show"
            :processing="modals.remove.processing"
            @close="closeRemoveModal"
            @confirm="confirmRemove"
        />
    </main>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useSessionStore } from '@/stores/session'
import AdminHeaderTabs from '@/screens/_components/admin/HeaderTabs.vue'
import CreateProjectSlideIn from '@/screens/trusted/admin/modals/CreateProjectSlideIn.vue'
import RemoveProjectModal from '@/screens/trusted/admin/modals/RemoveProjectModal.vue'
import ProjectService from '@/services/project.service'
import stringToKey from '@/helpers/string-to-key'
import { errorMessage } from '@/utils/notifications'
import { removeBy, upsertBy } from '@/utils/list-mutations'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'

// Store
const sessionStore = useSessionStore()
const { can } = usePermissions()

// State
const loading = ref(true)
const results = ref(null)

const modals = reactive({
    create: {
        show: false,
        processing: false,
        input: {
            name: null,
        },
    },
    remove: {
        show: false,
        processing: false,
        key: null,
    },
})

// Computed
const hasResults = computed(() => {
    return results.value != null && results.value.length > 0
})

// Methods
const refresh = async () => {
    loading.value = true
    try {
        const data = await ProjectService.list()
        results.value = data.data
    } catch (error) {
        errorMessage(error)
    } finally {
        loading.value = false
    }
}

const openCreateModal = () => {
    modals.create.show = true
}

const closeCreateModal = () => {
    modals.create.show = false
    modals.create.input.name = null
}

const confirmCreate = async () => {
    modals.create.processing = true
    try {
        const projectData = {
            name: modals.create.input.name,
            key: stringToKey(modals.create.input.name),
            description: '',
        }

        const data = await ProjectService.create(projectData)

        if (results.value === null) {
            results.value = []
        }

        const isFirstProject = results.value.length === 0

        if (isFirstProject) {
            sessionStore.setCurrent({
                projectKey: projectData.key,
                environmentKey: 'production',
            })
        }

        sessionStore.upsertProject(data.data)
        results.value = upsertBy(results.value, data.data, 'key')
        sessionStore.incrementProjectCount()
        closeCreateModal()
    } catch (error) {
        errorMessage(error)
    } finally {
        modals.create.processing = false
    }
}

const openRemoveModal = (key) => {
    modals.remove.key = key
    modals.remove.show = true
}

const closeRemoveModal = () => {
    modals.remove.show = false
    modals.remove.key = null
}

const confirmRemove = async () => {
    modals.remove.processing = true
    try {
        await ProjectService.remove(modals.remove.key)
        sessionStore.decrementProjectCount()
        sessionStore.removeProject(modals.remove.key)
        results.value = removeBy(results.value, modals.remove.key, 'key')
        closeRemoveModal()
    } catch (error) {
        errorMessage(error)
    } finally {
        modals.remove.processing = false
    }
}

// Lifecycle
onMounted(() => {
    refresh()
})
</script>
